import { FormEvent, useState } from "react";
import { heroStats, navLinks } from "@/mocks/home";

interface HeroProps {
  onSearch: (q: string) => void;
}

const heroBg =
  "https://readdy.ai/api/search-image?query=Warm%20abstract%20editorial%20collage%20of%20crumpled%20newspaper%20paper%20sheets%20speech%20bubbles%20and%20soft%20watermelon%20shapes%2C%20vibrant%20terracotta%20orange%20and%20cream%20and%20peach%20palette%2C%20floating%20light%20bokeh%2C%20modern%20digital%20illustration%20with%20subtle%20grain%2C%20high%20detail%2C%20no%20text%2C%20wide%20landscape%20composition&width=1800&height=900&seq=hero-suye-chigua-01&orientation=landscape";

export default function Hero({ onSearch }: HeroProps) {
  const [keyword, setKeyword] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    onSearch(q);
    document.getElementById("news")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative flex min-h-[680px] w-full items-center overflow-hidden">
      <img
        src={heroBg}
        alt="宿野吃瓜 热点讨论与热评观点聚合的背景插画"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/55 to-background-950/80"></div>

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-28 md:px-6 md:pb-20">
        <div className="mx-auto w-full max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur">
            <i className="ri-fire-fill text-secondary-400" aria-hidden="true"></i>
            全网热点 · 争议 · 观点聚合讨论区
          </span>

          <h1 className="mt-6 font-display text-4xl leading-tight text-background-50 sm:text-5xl md:text-6xl">
            宿野吃瓜 · 聊到点上
            <span className="mt-2 block text-secondary-300">围观也要有自己的立场</span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-background-200 md:text-lg">
            汇集各路瓜友的热评、观点与争议焦点。同一个瓜，有人吃得出甜头，有人看得出门道——
            不同看法在这里碰撞，帮你快速掌握舆论风向，再决定是下场发言还是安心围观。
          </p>

          <form
            onSubmit={submit}
            className="mx-auto mt-8 flex w-full max-w-xl flex-col gap-2 sm:flex-row"
            role="search"
            aria-label="站内话题搜索"
          >
            <label htmlFor="hero-search" className="sr-only">
              搜索吃瓜话题
            </label>
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-700 text-base" aria-hidden="true"></i>
              <input
                id="hero-search"
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜你想吃的话题，比如：直播带货 二创版权…"
                className="h-12 w-full rounded-md border-0 bg-background-50 pl-11 pr-4 text-sm text-foreground-950 outline-none ring-1 ring-transparent transition placeholder:text-foreground-600 focus:ring-2 focus:ring-secondary-400"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-secondary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
            >
              搜索讨论
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2" aria-label="热搜话题直达">
            {["直播带货翻车", "剧集口碑反转", "二创版权", "演唱会黄牛"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  onSearch(t);
                  setKeyword(t);
                  document.getElementById("news")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-full border border-background-50/30 bg-background-50/10 px-3 py-1 text-xs text-background-100 backdrop-blur transition-colors hover:bg-background-50/25"
              >
                <i className="ri-hashtag mr-1 text-secondary-400" aria-hidden="true"></i>
                {t}
              </button>
            ))}
          </div>
        </div>

        <dl className="mx-auto mt-12 grid w-full max-w-4xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-background-50/20 bg-background-50/10 backdrop-blur sm:grid-cols-4">
          {heroStats.map((s) => (
            <div key={s.label} className="bg-background-950/20 px-4 py-4 text-center">
              <dt className="text-xs text-background-300">{s.label}</dt>
              <dd className="mt-1 font-display text-2xl text-background-50">{s.value}</dd>
            </div>
          ))}
        </dl>

        <nav className="mx-auto mt-10 flex w-full flex-wrap items-center justify-center gap-3" aria-label="分区直达">
          {navLinks.slice(0, 3).map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              {l.label}
              <i className="ri-arrow-right-line ml-1.5" aria-hidden="true"></i>
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}